// The front page's lower half: what we build, the "Within Our Control" series, and the Charter.
import { ArrowRight } from "@phosphor-icons/react";
import { Link } from "react-router";

import { useCharter, usePosts } from "../../api/hooks";
import { CHARTER } from "../../content/charter";
import { PRODUCTS } from "../../content/products";
import { SectionHead } from "../../ui/Section";
import { PostCard } from "../blog/PostCard";

export function ProductsTeaser() {
  return (
    <section className="wrap py-20 sm:py-28">
      <SectionHead kicker="§ What we build" title="Products from a platform that runs real agent work every day." />
      <div className="mt-12 grid gap-4 lg:grid-cols-3">
        {PRODUCTS.map((p) => (
          <Link key={p.id} to={`/products#${p.id}`} className="card group flex flex-col p-6 transition-colors hover:border-ink sm:p-7">
            <div className="flex items-start justify-between gap-3">
              <p.Icon weight="duotone" className="h-9 w-9" aria-hidden />
              <span className="chip">{p.status.split(" · ")[0]}</span>
            </div>
            <h3 className="mt-7 font-display text-2xl font-bold tracking-tight">
              {p.name} {p.codename ? <span className="font-mono text-sm font-medium text-ink3">“{p.codename}”</span> : null}
            </h3>
            <p className="mt-1 font-serif text-lg italic text-ink2">{p.tagline}</p>
            <p className="mt-3 flex-1 text-[15px] leading-relaxed text-ink2">{p.body}</p>
            <span className="mt-6 inline-flex items-center gap-1.5 text-[14.5px] font-semibold group-hover:text-signal">
              Learn more <ArrowRight weight="bold" className="h-4 w-4" />
            </span>
          </Link>
        ))}
      </div>
    </section>
  );
}

export function SeriesTeaser() {
  const posts = usePosts();
  const series = (posts.data ?? []).filter((p) => p.series).sort((a, b) => (a.part ?? 0) - (b.part ?? 0));
  if (series.length === 0) return null;
  return (
    <section className="border-t border-line">
      <div className="wrap py-20 sm:py-28">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <SectionHead kicker={`§ The series · ${series.length} parts`} title={<>{series[0]!.series}.</>}>
            AI is inevitable. What we do about it isn't. Five essays on the parts that are still ours to decide.
          </SectionHead>
          <Link to="/blog" className="btn-line">
            All articles
          </Link>
        </div>
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {series.map((p, i) => (
            <PostCard key={p.slug} post={p} large={i === 0} />
          ))}
        </div>
      </div>
    </section>
  );
}

export function CharterTeaser() {
  const charter = useCharter();
  return (
    <section className="bg-ink text-paper">
      <div className="wrap grid gap-12 py-20 sm:py-28 lg:grid-cols-[1fr_1.2fr]">
        <div>
          <p className="font-mono text-[11.5px] uppercase tracking-[0.16em] text-paper/50">§ The Charter for Accountable AI</p>
          <h2 className="display mt-4 text-5xl sm:text-6xl">Seven things we ask of every AI company.</h2>
          <p className="mt-6 max-w-md font-serif text-xl text-paper/75">Not a law yet. A bar, set in public, that grows heavier with every name signed under it.</p>
          <div className="mt-9 flex flex-wrap items-center gap-5">
            <Link to="/charter#sign" className="btn-signal">
              Add your name <ArrowRight weight="bold" className="h-4 w-4" />
            </Link>
            {charter.data ? (
              <p className="font-mono text-sm text-paper/70">
                <span className="font-display text-3xl font-extrabold text-paper">{charter.data.signatures.toLocaleString("en-GB")}</span> signed so far
              </p>
            ) : null}
          </div>
        </div>
        <ol className="divide-y divide-paper/15 border-y border-paper/15">
          {CHARTER.map((p) => (
            <li key={p.n} className="grid grid-cols-[2.5rem_1fr] gap-3 py-4">
              <span className="font-mono text-sm text-signal">{String(p.n).padStart(2, "0")}</span>
              <div>
                <p className="font-display text-xl font-bold tracking-tight">{p.title}</p>
                <p className="mt-1 text-[15px] text-paper/65">{p.line}</p>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}
